import React, { useImperativeHandle } from 'react';
import Grid from '@mui/material/Grid';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { errorMessage } from 'constants/CommonConstants';
import { formRequiredRules } from 'helper';
import { CarInformation } from 'pages/tradeIn/TradeInInterface';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import CommonAutocomplete from 'sharedComponents/inputGroup/Autocomplete';
import { clearVehicleAutoCompleteData, vehicleLookupAutocomplete } from 'store/TradeInState';
import { RootState } from 'store/store';
import TradeInHeader from './header/TradeInHeader';

const Step3: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext, onBack }, ref) => {
  // Hooks
  const { t } = useTranslation();

  const hasCalledNextStep = React.useRef(false);

  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  // React Hook Form
  const { control, setValue } = useFormContext();

  const { vehicleAutoCompleteData } = useSelector((state: RootState) => state.tradeInStore);

  const onSuggestionSelected = (suggestion: CarInformation) => {
    const vehicleData = {
      year: suggestion.model_year,
      make: suggestion.make,
      model: suggestion.model,
      trim: suggestion.series,
      heading: suggestion.description,
      uvc: suggestion.uvc,
    };

    setValue('vehicleSearch', vehicleData);

    onNext?.();
  };

  const fetchOptions = async (searchText: string): Promise<[]> => {
    // Fetch filtered data based on the search text
    try {
      searchText.length < 2 && dispatch(clearVehicleAutoCompleteData());
      searchText.length > 2 && dispatch(vehicleLookupAutocomplete(searchText));
    } catch (error) {
      console.error('Error fetching search results:', error);
    }

    return [];
  };

  // React Hook Form Trigger
  const nextStep = async () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    onNext?.();
  };

  const backStep = () => {
    onBack?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
    backStep,
  }));

  return (
    <div className='dr-widget-steps d-flex flex-column h-100 step-2-1 position-relative'>
      <div className='dr-widget-steps__content'>
        <h2 className='dr-widget-steps__title'>{t('digitalRetailing.subHeading')}</h2>

        <TradeInHeader currentStep={0} />
        <div className='dr-widget-steps__form-wrapper'>
          <Grid container spacing={2}>
            <Grid item md={4} sm={8} xs={12}>
              <div className='form-group'>
                <CommonAutocomplete
                  control={control}
                  label={t('stepperFormTradeIn.freeTradeInEstimate')}
                  name='vehicleSearch'
                  fetchOptions={fetchOptions}
                  filteredData={vehicleAutoCompleteData}
                  placeholder={t('stepperFormTradeIn.searchHint')}
                  getSelectedVal={onSuggestionSelected}
                  textInputVariant='plain'
                  rules={formRequiredRules(errorMessage(t).required)}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
      <div className='dealer-send-note text-secondary mt-auto'>{t('stepperFormTradeIn.note4')}</div>
    </div>
  );
};

const StepperForm3 = React.forwardRef(Step3);

export default StepperForm3;
